import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useApi } from './ApiContext';

const MusicContext = createContext(null);

export const MusicProvider = ({ children }) => {
  const { API_BASE_URL } = useApi();
  const [musicData, setMusicData] = useState({
    soundcloud_tracks: [],
    apple_music_releases: [],
    soundcloud_profile: {},
    loading: true,
  });
  const hasFetched = useRef(false);

  useEffect(() => {
    // Prevent duplicate fetches (e.g. from StrictMode double-mount)
    if (hasFetched.current) return;
    hasFetched.current = true;

    const abortController = new AbortController();

    const loadMusic = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/music`, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data || typeof data !== 'object') {
          throw new Error('Invalid API response');
        }

        setMusicData({
          soundcloud_tracks: Array.isArray(data.soundcloud_tracks) ? data.soundcloud_tracks : [],
          apple_music_releases: Array.isArray(data.apple_music_releases) ? data.apple_music_releases : [],
          soundcloud_profile: data.soundcloud_profile || {},
          loading: false,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.log('Music fetch failed, using fallback data');
          setMusicData(prev => ({ ...prev, loading: false }));
        }
      }
    };

    loadMusic();

    return () => abortController.abort();
  }, [API_BASE_URL]);

  return (
    <MusicContext.Provider value={musicData}>
      {children}
    </MusicContext.Provider>
  );
};

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within MusicProvider');
  }
  return context;
};
